import {
  Box,
  Button,
  Divider,
  Flex,
  Stack,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import MapWithMarker from '../../../components/map-with-marker/MapWithMarker';
import { Practice } from '../../../types/practices/types';
import MarketAbsencesList from '../state/MarketAbsencesList';
import MarketPlansList from '../state/MarketPlansList';
import MarketPracticeList from '../state/MarketPracticeList';
import MarketProviderList from '../state/MarketProviderList';
import MarketClinicClosures from './MarketClinicClosures';
import SickVisitAvailability from './SickVisitAvailability';

interface Location {
  address?: string;
  name?: string;
  id?: string;
  lat?: number | undefined;
  lng?: number | undefined;
  active?: boolean;
  practiceOnPac?: boolean | string | null;
  practiceType?: string;
}

interface MarketResourcesSectionProps {
  marketId: string;
  marketName?: string;
  practices: Practice[];
  practicesLoading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPracticesPageChange: (page: number) => void;
  plansCount: number;
  practiceCount: number;
  providerCount: number;
  clinicClosuresCount?: number;
  providerAbsencesCount?: number;
  locations?: Location[];
}

const MarketResourcesSection: React.FC<
  MarketResourcesSectionProps
> = ({
  marketId,
  marketName,
  practices,
  practicesLoading,
  currentPage,
  totalPages,
  totalCount,
  onPracticesPageChange,
  plansCount,
  practiceCount,
  providerCount,
  clinicClosuresCount,
  providerAbsencesCount,
  locations = [],
}) => {
  const [selectedView, setSelectedView] = useState<
    | 'practices'
    | 'providers'
    | 'plans'
    | 'sickVisits'
    | 'clinicClosures'
    | 'absences'
    | 'map'
  >('practices');

  // Tab badges now show the true totals from market data, not dynamic counts

  // Show sick visits only for PCP markets
  const shouldShowSickVisits = marketName
    ?.toLowerCase()
    .includes('pcp');

  // If sick visits is hidden and currently selected, switch to practices
  useEffect(() => {
    if (!shouldShowSickVisits && selectedView === 'sickVisits') {
      setSelectedView('practices');
    }
  }, [shouldShowSickVisits, selectedView]);

  // Lazy load content - only render active tab to prevent unnecessary API calls
  const content = useMemo(() => {
    if (!marketId) return null;

    // Only render the currently selected view to prevent mass API calls
    switch (selectedView) {
      case 'practices':
        return (
          <MarketPracticeList
            marketId={[marketId]}
            practices={practices}
            loading={practicesLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={onPracticesPageChange}
          />
        );
      case 'providers':
        return <MarketProviderList marketId={[marketId]} />;
      case 'plans':
        return (
          <MarketPlansList
            marketId={[marketId]}
            name={marketName || 'Market'}
          />
        );
      case 'sickVisits':
        // Don't render sick visits content if it should be hidden
        if (!shouldShowSickVisits) {
          return (
            <MarketPracticeList
              marketId={[marketId]}
              practices={practices}
              loading={practicesLoading}
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={onPracticesPageChange}
            />
          );
        }
        return <SickVisitAvailability marketId={marketId} />;
      case 'clinicClosures':
        return <MarketClinicClosures marketId={marketId} />;
      case 'absences':
        return <MarketAbsencesList marketId={[marketId]} />;
      case 'map':
        return (
          <Box
            width="100%"
            height="600px"
            borderRadius="0.5rem"
            overflow="hidden"
          >
            {locations && locations.length > 0 ? (
              <MapWithMarker locations={locations} />
            ) : (
              <Flex
                direction="column"
                align="center"
                justify="center"
                height="100%"
                textAlign="center"
                bg="gray.50"
                borderRadius="0.5rem"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text
                  fontSize="1.125rem"
                  fontWeight="600"
                  color="text.secondary"
                  mb="0.5rem"
                >
                  No Map Available
                </Text>
                <Text
                  fontSize="0.875rem"
                  color="gray.600"
                  mb="1.5rem"
                  maxW="300px"
                >
                  No practices have been added to this market yet.
                </Text>
              </Flex>
            )}
          </Box>
        );
      default:
        return null;
    }
  }, [
    marketId,
    selectedView,
    marketName,
    shouldShowSickVisits,
    locations,
    practices,
    practicesLoading,
    currentPage,
    totalPages,
    totalCount,
    onPracticesPageChange,
  ]);

  return (
    <Box ml="2rem" mr="2rem">
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb="1rem"
      >
        <Flex gap="1rem">
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'practices'
                ? 'background.blue100'
                : 'none'
            }
            onClick={() => setSelectedView('practices')}
            color={
              selectedView === 'practices'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'practices' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Practices {practiceCount}
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'providers'
                ? 'background.blue100'
                : 'none'
            }
            onClick={() => setSelectedView('providers')}
            color={
              selectedView === 'providers'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'providers' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Providers {providerCount}
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'plans' ? 'background.blue100' : 'none'
            }
            onClick={() => setSelectedView('plans')}
            color={
              selectedView === 'plans'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'plans' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Plans {plansCount}
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'sickVisits'
                ? 'background.blue100'
                : 'none'
            }
            onClick={() => setSelectedView('sickVisits')}
            color={
              selectedView === 'sickVisits'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'sickVisits' ? 600 : 500}
            lineHeight={'1.25rem'}
            display={shouldShowSickVisits ? 'block' : 'none'}
          >
            Sick Visits
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'clinicClosures'
                ? 'background.blue100'
                : 'none'
            }
            onClick={() => setSelectedView('clinicClosures')}
            color={
              selectedView === 'clinicClosures'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'clinicClosures' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Clinic Closures {clinicClosuresCount}
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'absences'
                ? 'background.blue100'
                : 'none'
            }
            onClick={() => setSelectedView('absences')}
            color={
              selectedView === 'absences'
                ? 'text.blue700'
                : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'absences' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Absences {providerAbsencesCount}
          </Button>
          <Button
            height={'1.75rem'}
            padding={'0.25rem 1rem'}
            borderRadius={'624.9375rem'}
            background={
              selectedView === 'map' ? 'background.blue100' : 'none'
            }
            onClick={() => setSelectedView('map')}
            color={
              selectedView === 'map' ? 'text.blue700' : 'text.primary'
            }
            fontSize={'0.875rem'}
            fontWeight={selectedView === 'map' ? 600 : 500}
            lineHeight={'1.25rem'}
          >
            Map
          </Button>
        </Flex>
      </Flex>
      <Divider mb="1rem" />
      <Box>
        <Stack gap="1rem">{content}</Stack>
      </Box>
    </Box>
  );
};

export default MarketResourcesSection;
